import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'

/**
 * Detalle de factura. Query propia (no generada por codegen, que solo escanea los
 * `.graphql`). Sus campos están alineados con el tipo `PortalBilling` del gateway:
 * `PortalBillingPayment` expone `paymentGatewayId` (no un objeto `paymentGateway`)
 * y `PortalBillingItem` no tiene `quantity`.
 */
const PORTAL_BILLING = gql`
  query PortalBilling($billingId: Int!) {
    portalBilling(billingId: $billingId) {
      id
      invoiceNumber
      endsAt
      startsAt
      status
      observations
      currencyId
      currency {
        id
        name
      }
      registeredAt
      discountAmount
      businessId
      organizationId
      billingPayments {
        id
        amount
        currencyId
        paymentGatewayId
        observation
      }
      billingItems {
        id
        amount
        currencyId
        description
        startsAt
        endsAt
      }
      totalPrices {
        id
        amount
        currencyId
      }
    }
  }
`

export interface BillingItem {
  id: number
  amount: number
  currencyId?: number | null
  description?: string | null
  startsAt?: string | null
  endsAt?: string | null
}

export interface BillingPayment {
  id: number
  amount: number
  currencyId?: number | null
  paymentGatewayId?: number | null
  observation?: string | null
}

export interface BillingTotalPrice {
  id: number
  amount: number
  currencyId: number
}

export interface BillingDetail {
  id: number
  invoiceNumber: string
  endsAt?: string | null
  startsAt?: string | null
  status: string
  observations?: string | null
  currencyId?: number | null
  currency?: { id: number; name: string } | null
  registeredAt?: string | null
  discountAmount?: number | null
  businessId?: string | null
  organizationId?: string | null
  billingPayments: BillingPayment[]
  billingItems: BillingItem[]
  totalPrices: BillingTotalPrice[]
}

interface PortalBillingResult {
  portalBilling: BillingDetail | null
}

/** Detalle de una factura por id. */
export function useBilling(billingId: number | undefined) {
  const { data, loading, error, refetch } = useQuery<PortalBillingResult>(PORTAL_BILLING, {
    variables: { billingId: billingId ?? 0 },
    skip: billingId == null || Number.isNaN(billingId),
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  })

  return {
    billing: data?.portalBilling ?? null,
    loading,
    error,
    refetch,
  }
}
